/**
 * BrainLogsPage — Full-page Brain log viewer for a single tenant
 *
 * Route: /admin/brain-logs?tenantId=xxx&tenantName=Acme+Corp
 * Opens as a dedicated MDI tab from BrainMonitorPage.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  RefreshCw, Loader2, Search, X, Radio,
  FileText, AlertTriangle,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LogEntry {
  id: string;
  tenant_id: string | null;
  level: 'info' | 'warn' | 'error';
  category: string;
  message: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

// ─── Level style map ─────────────────────────────────────────────────────────

const LEVEL_BADGE: Record<string, string> = {
  info: 'bg-sky-100 text-sky-700 border-sky-200',
  warn: 'bg-amber-100 text-amber-700 border-amber-200',
  error: 'bg-red-100 text-red-700 border-red-200',
};

// ─── Log Row ──────────────────────────────────────────────────────────────────

function LogRow({ log }: { log: LogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasMetadata = log.metadata && Object.keys(log.metadata).length > 0;

  return (
    <div
      className={`group flex items-start gap-3 px-4 py-2 text-xs font-mono border-b border-border/30 hover:bg-muted/40 transition-colors ${
        log.level === 'error' ? 'bg-red-50/40 dark:bg-red-950/10' :
        log.level === 'warn'  ? 'bg-amber-50/30 dark:bg-amber-950/10' : ''
      }`}
    >
      {/* Time */}
      <span className="shrink-0 w-[80px] text-muted-foreground tabular-nums pt-0.5 select-none">
        {new Date(log.created_at).toLocaleTimeString('en-US', { hour12: false })}
      </span>

      {/* Level badge */}
      <span className={`shrink-0 w-12 text-center rounded border px-1 py-px text-[10px] font-bold uppercase ${LEVEL_BADGE[log.level] ?? 'bg-muted text-muted-foreground'}`}>
        {log.level}
      </span>

      {/* Category */}
      <span className="shrink-0 w-[130px] truncate text-violet-600 dark:text-violet-400 pt-0.5">
        [{log.category}]
      </span>

      {/* Message + optional metadata */}
      <div className="flex-1 min-w-0">
        <div
          className={`text-foreground leading-snug whitespace-pre-wrap break-words ${hasMetadata ? 'cursor-pointer' : ''}`}
          onClick={() => hasMetadata && setExpanded(v => !v)}
        >
          {log.message}
          {hasMetadata && (
            <span className="ml-2 text-muted-foreground opacity-60 group-hover:opacity-100 text-[10px] font-sans not-italic">
              {expanded ? '▲ hide' : '▶ details'}
            </span>
          )}
        </div>

        {expanded && hasMetadata && (
          <pre className="mt-1.5 rounded bg-muted/60 border px-3 py-2 text-[10px] text-muted-foreground overflow-auto max-h-40 leading-relaxed">
            {JSON.stringify(log.metadata, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function BrainLogsPage() {
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId') ?? '';
  const tenantName = searchParams.get('tenantName') ?? tenantId;

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<'all' | 'info' | 'warn' | 'error'>('all');
  const [sseOn, setSseOn] = useState(false);
  const sseRef = useRef<EventSource | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const fetchLogs = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<LogEntry[]>(`/platform/tenants/${tenantId}/brain/logs?limit=100`);
      setLogs(Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void fetchLogs();
    return () => { sseRef.current?.close(); sseRef.current = null; };
  }, [fetchLogs]);

  const toggleSse = useCallback(() => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
      setSseOn(false);
      return;
    }
    const es = new EventSource(`${BASE}/monitoring/brain/stream?tenantId=${tenantId}`);
    const handleLog = (e: MessageEvent) => {
      try {
        const entry = JSON.parse(e.data as string) as LogEntry;
        setLogs(prev => [...prev, entry].slice(-1000));
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      } catch { /* ignore */ }
    };
    es.addEventListener('log', handleLog);
    es.onerror = () => { sseRef.current?.close(); sseRef.current = null; setSseOn(false); };
    sseRef.current = es;
    setSseOn(true);
  }, [tenantId]);

  // ── Filter ───────────────────────────────────────────────────────────────
  const filtered = logs.filter(l => {
    if (levelFilter !== 'all' && l.level !== levelFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        l.message.toLowerCase().includes(q) ||
        l.category.toLowerCase().includes(q) ||
        (l.metadata ? JSON.stringify(l.metadata).toLowerCase().includes(q) : false)
      );
    }
    return true;
  });

  const counts = {
    info:  logs.filter(l => l.level === 'info').length,
    warn:  logs.filter(l => l.level === 'warn').length,
    error: logs.filter(l => l.level === 'error').length,
  };

  if (!tenantId) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
        <FileText className="h-10 w-10 opacity-30" />
        <p className="text-sm font-medium">未指定租户</p>
        <p className="text-xs">请从 <a href="/admin/brain-monitor" className="text-primary underline hover:no-underline">Brain Monitor</a> 页面点击某个租户的「Logs」按钮打开此页。</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-5 py-3 border-b bg-background/95 backdrop-blur flex-shrink-0">
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="flex flex-col leading-tight mr-2">
          <span className="font-semibold text-sm">{tenantName}</span>
          {tenantName !== tenantId && <span className="text-[10px] text-muted-foreground font-mono">{tenantId.slice(0, 8)}…</span>}
        </div>

        {/* Level filter pills */}
        <div className="flex items-center gap-1.5 ml-1">
          {(['all', 'info', 'warn', 'error'] as const).map(lvl => {
            const cnt = lvl === 'all' ? logs.length : counts[lvl];
            return (
              <button
                key={lvl}
                onClick={() => setLevelFilter(lvl)}
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${
                  levelFilter === lvl
                    ? lvl === 'error' ? 'bg-red-600 text-white border-red-600'
                    : lvl === 'warn'  ? 'bg-amber-500 text-white border-amber-500'
                    : lvl === 'info'  ? 'bg-sky-600 text-white border-sky-600'
                    : 'bg-foreground text-background border-foreground'
                    : 'bg-transparent text-muted-foreground border-border hover:border-muted-foreground'
                }`}
              >
                {lvl.toUpperCase()}
                {cnt > 0 && <span className="ml-1 opacity-75">{cnt}</span>}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-8 h-8 text-sm bg-muted/40"
            placeholder="Search message, category, metadata…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setSearch('')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Live connect */}
        <Button
          size="sm"
          variant={sseOn ? 'default' : 'outline'}
          className={`h-8 gap-1.5 text-xs ${sseOn ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
          onClick={toggleSse}
        >
          <Radio className={`h-3.5 w-3.5 ${sseOn ? 'animate-pulse' : ''}`} />
          {sseOn ? 'Live' : 'Connect'}
        </Button>

        {/* Refresh */}
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => void fetchLogs()} disabled={loading}>
          {loading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>

        {/* Result count */}
        <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
          {filtered.length}{filtered.length !== logs.length ? `/${logs.length}` : ''} entries
        </span>
      </div>

      {/* ── Error ────────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-2 px-5 py-2.5 text-sm text-destructive bg-destructive/10 border-b border-destructive/20 flex-shrink-0">
          <AlertTriangle className="h-4 w-4 shrink-0" />{error}
        </div>
      )}

      {/* ── Column header ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-1.5 border-b bg-muted/20 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex-shrink-0">
        <span className="w-[80px] shrink-0">Time</span>
        <span className="w-12 shrink-0">Level</span>
        <span className="w-[130px] shrink-0">Category</span>
        <span className="flex-1">Message</span>
      </div>

      {/* ── Log list ─────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {loading && !logs.length && (
          <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />Loading logs…
          </div>
        )}

        {!loading && !filtered.length && (
          <div className="py-20 text-center text-muted-foreground text-sm">
            {search || levelFilter !== 'all'
              ? `No logs match the current filter`
              : 'No logs found for this tenant'}
          </div>
        )}

        {filtered.map(log => <LogRow key={log.id} log={log} />)}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
